import { Dispatch, Reducer, useState } from "react";
import createPersistedState from "./createPersistedState";
import {
  EnvData,
  Fact,
  NonEmptyArray,
  PolicyTestResult,
  Result,
  TypedId,
  Option,
} from "./types";

export type Relation = {
  // define the relation in the relations type of the resource block
  // relations: { parent: ParentOrg }
  relation_name: string;
  related_resource: string;
  /** rows are roles on this resource, columns are roles on the related resource */
  matrix: boolean[][];
};

export type ResourceBlock = {
  name: string;
  roles: string[];
  perms: string[];
  /** array of rows. Columns are roles, Rows are permissions.
   outer length = perms length; inner length = roles length */
  matrix: boolean[][];
  rolesImplicationMatrix: boolean[][];
  relations: Relation[];
  globalRolesMatrix: boolean[][];
};

export type ResourceBlocks = ResourceBlock[];

export type GlobalBlock = {
  roles: string[];
  perms: string[];
  /** array of rows. Columns are roles, rows are permissions. */
  matrix: boolean[][];
  /** square matrix of role inheritance; columns imply rows */
  rolesImplicationMatrix: boolean[][];
};

export type TestAssertion = {
  expected: boolean;
  fact: Fact;
};

export type TestBlock = {
  name: string;
  setup: Fact[];
  assertions: TestAssertion[];
};

export type TestBlocks = TestBlock[];

// Are you adding fields to this? Please make sure the invariants in
// `isDefaultWorkbenchState` still hold.
export type ActorBlock = {
  name: string;
};

export type ActorBlocks = NonEmptyArray<ActorBlock>;

export type Rule = {
  description: string;
  source: string;
};

export type Rules = Rule[];

export type WorkbenchPolicy = {
  workbenchVersion: string;
  actors: ActorBlocks;
  global: Option<GlobalBlock>;
  resources: ResourceBlocks;
  rules: Rules;
  tests: TestBlocks;
};

/**
 * If we detect a policy that's completely empty, use the default workbench
 * policy. The default workbench policy has a default actor set, and a bunch
 * of code assumes that it is there, but sometimes we can get a completely
 * empty policy back from the API.
 */
function defaultPolicyIfEmpty(policy: WorkbenchPolicy) {
  let field: keyof WorkbenchPolicy;
  for (field in policy) {
    if (field === "workbenchVersion") {
      continue;
    }
    if (field === "global") {
      if (policy[field] !== null) {
        return policy;
      } else {
        continue;
      }
    }
    if (policy[field].length !== 0) {
      return policy;
    }
  }
  return DEFAULT_WORKBENCH_POLICY;
}

const CURRENT_WORKBENCH_STATE_VERSION: string = "0.4.0";

type WorkbenchStateMigration = {
  version: string;
  /** migrates from the next most recent version to this version. */
  migration: (state: any) => any;
};

/** chronologically ordered */
const WORKBENCH_STATE_MIGRATIONS: WorkbenchStateMigration[] = [
  {
    version: "0.1.0",
    migration(state: any): any {
      const resources = state.map((resource: any) => ({
        ...resource,
        // `globalRolesMatrix` was added in 0.1.0
        globalRolesMatrix: [],
        // prior to 0.1.0, the rows of Relation.matrix were permissions on the current resource.
        // in 0.1.0, this was changed to roles on the current resource.
        relations: resource.relations.map((relation: any) => {
          const relatedResourceRoles = state.find(
            ({ name }: any) => name === relation.related_resource
          ).roles;
          return {
            ...relation,
            matrix: resource.roles.map((_: any) =>
              relatedResourceRoles.map((_: any) => false)
            ),
          };
        }),
      }));
      return {
        global: null,
        resources,
      };
    },
  },
  {
    version: "0.2.0",
    migration(state: any): any {
      // `workbenchPolicy.tests` was added in 0.2.0
      return { ...state, tests: [] };
    },
  },
  {
    version: "0.3.0",
    migration(state: any): any {
      // `workbenchPolicy.actors` was added in 0.3.0
      return { ...state, actors: DEFAULT_WORKBENCH_POLICY.actors };
    },
  },
  {
    version: "0.4.0",
    migration(state: any): any {
      // `workbenchPolicy.rules` was added in 0.4.0
      return { ...state, rules: [] };
    },
  },
];

const migrateWorkbenchState = (state: WorkbenchPolicy): WorkbenchPolicy => ({
  ...WORKBENCH_STATE_MIGRATIONS.slice(
    WORKBENCH_STATE_MIGRATIONS.findIndex(
      ({ version }) => version === state.workbenchVersion
    ) + 1
  ).reduce((state, { migration }) => migration(state), state),
  workbenchVersion: CURRENT_WORKBENCH_STATE_VERSION,
});

// Are you updating this? Make sure the invariants in `isDefaultWorkbenchState`
// still hold, so that isDefaultWorkbenchState(DEFAULT_WORKBENCH_POLICY) === true
export const DEFAULT_WORKBENCH_POLICY: WorkbenchPolicy = {
  workbenchVersion: CURRENT_WORKBENCH_STATE_VERSION,
  actors: [{ name: "User" }],
  global: null,
  resources: [],
  rules: [],
  tests: [],
};

// wrapper around Object.entries to make it more pleasant in Typescript
function objectEntries<
  T extends Record<PropertyKey, unknown>,
  K extends keyof T,
  V extends T[K]
>(o: T) {
  return Object.entries(o) as [K, V][];
}

/**
 * Is the given state the current default workbench state?
 * Used to determine whether the user has meaningfully interacted with workbench.
 */
export function isDefaultWorkbenchState(state: WorkbenchPolicy): boolean {
  return objectEntries(state).every(([key, value]): boolean => {
    const defaultValue = DEFAULT_WORKBENCH_POLICY[key];

    // Good enough for now- easier than implementing general deep equality.

    // INVARIANT: ActorBlock's properties are all primitives, not objects.
    // In other words, two ActorBlock-s are equivalent if
    // all of their properties === each other.
    //
    // INVARIANT: `actors` is the only property on DEFAULT_WORKBENCH_POLICY that
    // is anything other than an empty array or a primitive.
    if (key === "actors") {
      let defaultActorBlocks = defaultValue as ActorBlocks;
      let actorBlocks = value as ActorBlocks;
      return (
        defaultActorBlocks.length === actorBlocks.length &&
        defaultActorBlocks.every((block, i) => {
          const otherBlock = actorBlocks[i];
          return objectEntries(block).every(([blockKey, blockValue]) => {
            return blockValue === otherBlock[blockKey];
          });
        })
      );
    } else if (
      value instanceof Array &&
      defaultValue instanceof Array &&
      defaultValue.length === 0
    ) {
      return value.length === 0;
    } else {
      //assume it's a primitive
      return defaultValue === value;
    }
  });
}

export type WorkbenchPolicyAction =
  | {
      action: "add role";
      resource: number;
      role: string;
    }
  | { action: "add perm"; resource: number; perm: string }
  | {
      action: "add relation";
      resource: number;
      relation: string;
      related_resource: string;
    }
  | { action: "reset" }
  | { action: "add resource"; resource: string }
  | {
      action: "set perms checkbox";
      resource: number;
      i: number;
      j: number;
      value: boolean;
    }
  | {
      action: "set roles checkbox";
      resource: number;
      i: number;
      j: number;
      value: boolean;
    }
  | {
      action: "set relations checkbox";
      resource: number;
      relation: number;
      i: number;
      j: number;
      value: boolean;
    }
  | { action: "edit perm"; resource: number; perm: number; name: string }
  | { action: "edit role"; resource: number; role: number; name: string }
  | { action: "edit resource name"; resource: number; name: string }
  | {
      action: "edit resource relation name";
      resource: number;
      relation_name: string;
      updated_name: string;
    }
  | {
      action: "delete resource relation";
      resource: number;
      relation_name: string;
    }
  | { action: "delete resource"; resource: number }
  | { action: "delete role"; resource: number; role: number }
  | { action: "delete perm"; resource: number; perm: number }
  | {
      action: "add global role";
      role: string;
    }
  | { action: "add global perm"; perm: string }
  | { action: "add global block" }
  | { action: "delete global block" }
  | {
      action: "set global perms checkbox";
      i: number;
      j: number;
      value: boolean;
    }
  | {
      action: "set global roles checkbox";
      i: number;
      j: number;
      value: boolean;
    }
  | { action: "edit global perm"; perm: number; name: string }
  | { action: "edit global role"; role: number; name: string }
  | { action: "delete global role"; role: number }
  | { action: "delete global perm"; perm: number }
  | {
      action: "set resource role global role checkbox";
      resource: number;
      i: number;
      j: number;
      value: boolean;
    }
  | { action: "add test block"; name: string; replace: boolean }
  | { action: "edit test block"; block: number; name: string }
  | { action: "delete test block"; block: number }
  | { action: "add test setup fact"; block: number; fact: Fact }
  | { action: "edit test setup fact"; block: number; fact: number; value: Fact }
  | { action: "delete test setup fact"; block: number; fact: number }
  | { action: "add test assertion"; block: number; assertion: TestAssertion }
  | {
      action: "edit test assertion";
      block: number;
      assertion: number;
      value: TestAssertion;
    }
  | { action: "delete test assertion"; block: number; assertion: number }
  | { action: "add actor"; name: string }
  | { action: "edit actor name"; actor: number; name: string }
  | { action: "delete actor"; actor: number }
  | { action: "add rule"; description: string; source: string }
  | { action: "edit rule description"; rule: number; description: string }
  | { action: "delete rule"; rule: number }
  | {
      /** TODO: return early upon errors */
      action: "batch";
      actions: WorkbenchPolicyAction[];
    };

export const EXAMPLE_USER_NAMES: string[] = [
  "alice",
  "bob",
  "charlie",
  "dave",
  "eve",
  "frankie",
  "george",
  "harper",
  "idris",
  "jamie",
];

function exampleUserName(id: number): string {
  if (id >= EXAMPLE_USER_NAMES.length) {
    return `user${id - EXAMPLE_USER_NAMES.length}`;
  } else {
    return EXAMPLE_USER_NAMES[id];
  }
}

// PicoKanren
export function roleImpliesRole(
  block: {
    rolesImplicationMatrix: boolean[][];
    roles: string[];
  },
  role: number,
  impliedRole: number
): boolean {
  // memoized to prevent divergence
  const memo_table = new Map<string, boolean>();
  function helper(role: number, impliedRole: number): boolean {
    const memo_key = `${role}_${impliedRole}`;
    const memo_entry = memo_table.get(memo_key);
    if (memo_entry !== undefined) {
      return memo_entry;
    } else {
      // set the current memo entry to false while recurring so that
      // if we end up looping back to the same entry in a recursive call
      // we return false there and stop searching
      memo_table.set(memo_key, false);
    }
    const result =
      role === impliedRole ||
      block.roles.some(
        (_, cutRole) =>
          block.rolesImplicationMatrix[cutRole][role] &&
          helper(cutRole, impliedRole)
      );
    memo_table.set(memo_key, result);
    return result;
  }
  return helper(role, impliedRole);
}

export function roleGrantsPermission(
  block: {
    matrix: boolean[][];
    rolesImplicationMatrix: boolean[][];
    roles: string[];
  },
  role: number,
  perm: number
): boolean {
  return block.roles.some(
    (_, impliedRoleIdx) =>
      block.matrix[perm][impliedRoleIdx] &&
      roleImpliesRole(block, role, impliedRoleIdx)
  );
}

export function workbenchToPolicy(
  workbenchPolicy: WorkbenchPolicy,
  options?: { skipTests?: boolean }
) {
  const actorPolicy = (actor: ActorBlock): string => `actor ${actor.name} {}`;

  const resourcePolicy = (resource: ResourceBlock): string => {
    const {
      name,
      roles,
      perms,
      matrix,
      rolesImplicationMatrix,
      relations,
      globalRolesMatrix,
    } = resource;

    const rolesPolicy: string[] =
      roles.length > 0
        ? [`  roles = [${roles.map((role) => `"${role}"`).join(", ")}];`]
        : [];

    const permsPolicy: string[] =
      perms.length > 0
        ? [`  permissions = [${perms.map((perm) => `"${perm}"`).join(", ")}];`]
        : [];

    const relationsPolicy: string[] =
      relations.length > 0
        ? [
            `  relations = { ${relations
              .map(
                ({ relation_name, related_resource }) =>
                  `${relation_name}: ${related_resource}`
              )
              .join(", ")} };`,
          ]
        : [];

    const globalRulesPolicy = globalRolesMatrix.flatMap((row, i) =>
      row.flatMap((checked, j) =>
        checked
          ? [
              `  "${roles[i]}" if global "${workbenchPolicy.global?.roles?.[j]}";`,
            ]
          : []
      )
    );

    const rulesPolicy: string[] = matrix.some((row) =>
      row.some((checked) => checked)
    )
      ? [
          ...matrix.flatMap((perm_row, i) =>
            perm_row.flatMap((checked, j) =>
              checked ? [`  "${perms[i]}" if "${roles[j]}";`] : []
            )
          ),
        ]
      : [];

    const rolesImplicationsRulesPolicy: string[] = rolesImplicationMatrix.some(
      (row) => row.some((checked) => checked)
    )
      ? [
          ...rolesImplicationMatrix.flatMap((perm_row, i) =>
            perm_row.flatMap((checked, j) =>
              checked ? [`  "${roles[i]}" if "${roles[j]}";`] : []
            )
          ),
        ]
      : [];

    const relationRulesPolicy: string[] = relations.flatMap(
      ({ relation_name, related_resource, matrix }) => {
        const related_roles = workbenchPolicy.resources.find(
          ({ name }) => name === related_resource
        )?.roles;
        return related_roles
          ? matrix.flatMap((perm_row, i) =>
              perm_row.flatMap((checked, j) =>
                checked
                  ? [
                      `  "${roles[i]}" if "${related_roles[j]}" on "${relation_name}";`,
                    ]
                  : []
              )
            )
          : [];
      }
    );

    const allRulesPolicy = [
      ...globalRulesPolicy,
      ...rulesPolicy,
      ...rolesImplicationsRulesPolicy,
      ...relationRulesPolicy,
    ];

    return [
      `resource ${name} {`,
      ...rolesPolicy,
      ...permsPolicy,
      ...relationsPolicy,
      ...(allRulesPolicy.length > 0 ? ["", ...allRulesPolicy] : []),
      "}",
    ].join("\n");
  };

  const rulePolicy = ({ description, source }: Rule) =>
    `# ${description}\n${source}`;

  const testBlockPolicy = ({ name, setup, assertions }: TestBlock): string => {
    if (assertions.length === 0) {
      return "";
    } else {
      // only render setup if non-empty
      const setupBlock: string[] =
        setup.length === 0
          ? []
          : [
              "  setup {",
              ...setup.map((fact) => `    ${factToPolicy(fact)};`),
              "  }",
            ];
      return [
        `test "${name}" {`,
        ...setupBlock,
        ...assertions.map(
          ({ expected, fact }) =>
            `  assert${expected ? "    " : "_not"} ${factToPolicy(fact)};`
        ),
        "}",
      ].join("\n");
    }
  };

  const policyParts: string[] = [];

  if (workbenchPolicy.global) {
    const { roles, perms, matrix, rolesImplicationMatrix } =
      workbenchPolicy.global;
    const rolesPolicy: string[] =
      roles.length > 0
        ? [`  roles = [${roles.map((role) => `"${role}"`).join(", ")}];`]
        : [];

    const permsPolicy: string[] =
      perms.length > 0
        ? [`  permissions = [${perms.map((perm) => `"${perm}"`).join(", ")}];`]
        : [];

    const rulesPolicy: string[] = matrix.some((row) =>
      row.some((checked) => checked)
    )
      ? [
          "",
          ...matrix.flatMap((perm_row, i) =>
            perm_row.flatMap((checked, j) =>
              checked ? [`  "${perms[i]}" if "${roles[j]}";`] : []
            )
          ),
        ]
      : [];

    const rolesRulesPolicy: string[] = rolesImplicationMatrix.some((row) =>
      row.some((checked) => checked)
    )
      ? [
          ...rolesImplicationMatrix.flatMap((perm_row, i) =>
            perm_row.flatMap((checked, j) =>
              checked ? [`  "${roles[i]}" if "${roles[j]}";`] : []
            )
          ),
        ]
      : [];

    policyParts.push(
      [
        "global {",
        ...rolesPolicy,
        ...permsPolicy,
        ...rulesPolicy,
        ...rolesRulesPolicy,
        "}",
      ].join("\n")
    );
  }

  for (const actor of workbenchPolicy.actors) {
    policyParts.push(actorPolicy(actor));
  }

  for (const resource of workbenchPolicy.resources) {
    policyParts.push(resourcePolicy(resource));
  }

  for (const rule of workbenchPolicy.rules) {
    policyParts.push(rulePolicy(rule));
  }
  if (!options?.skipTests) {
    for (const testBlock of workbenchPolicy.tests) {
      policyParts.push(testBlockPolicy(testBlock));
    }
  }

  return policyParts.join("\n\n");
}

export function updateIdx<T>(
  arr: NonEmptyArray<T>,
  i: number,
  value: T
): NonEmptyArray<T>;
export function updateIdx<T>(arr: T[], i: number, value: T): T[];
export function updateIdx<T>(arr: T[], i: number, value: T): T[] {
  return [...arr.slice(0, i), value, ...arr.slice(i + 1)];
}

export function deleteIdx<T>(arr: T[], i: number): T[] {
  return [...arr.slice(0, i), ...arr.slice(i + 1)];
}

/** Strings, Booleans, and Integers have special shorthand syntax for their literals.
 *  This function pretty-prints a TypedId as a Polar literal, using the shorthand syntax if possible. */
export function typedIdToPolicy({ type, id }: TypedId): string {
  switch (type) {
    case "String":
      return `"${id}"`;
    case "Boolean":
    case "Integer":
      return id;
    default:
      return `${type}{"${id}"}`;
  }
}
export const factToPolicy = ({ predicate, args }: Fact): string =>
  `${predicate}(${args.map(typedIdToPolicy).join(", ")})`;

function checkIfValidDeclaration(
  resource: ResourceBlock,
  declaration: string
): Option<string> {
  if (!declaration) {
    return "Name cannot be empty.";
  }
  if (resource.roles.some((r) => r === declaration)) {
    return `Can't add "${declaration}" as resource ${resource.name} already has a role named "${declaration}"`;
  }
  if (resource.perms.some((p) => p === declaration)) {
    return `Can't add "${declaration}" as resource ${resource.name} already has a permission named "${declaration}"`;
  }
  if (
    resource.relations.some(
      ({ relation_name }) => relation_name === declaration
    )
  ) {
    return `Can't add "${declaration}" as resource ${resource.name} already has a relation named "${declaration}"`;
  }
  return null;
}

function checkIfValidGlobalDeclaration(
  global: GlobalBlock,
  declaration: string
): Option<string> {
  if (!declaration) {
    return "Name can't be empty";
  }
  if (global.roles.some((r) => r === declaration)) {
    return `Can't add "${declaration}" as there is already a global role with that name`;
  }
  if (global.perms.some((p) => p === declaration)) {
    return `Can't add "${declaration}" as there is already a global permission with that name`;
  }
  return null;
}

export function capitalize(word: string): string {
  return word.charAt(0).toUpperCase() + word.slice(1);
}

export function decapitalize(word: string): string {
  return word.charAt(0).toLowerCase() + word.slice(1);
}

export function startsWithCapital(word: string): boolean {
  return (
    !word.match(/^[0-9]/) && word.charAt(0) === word.charAt(0).toUpperCase()
  );
}

function startsWithLetter(word: string): boolean {
  return !word.match(/^[0-9]/);
}

function checkIfValidRelation(
  resource: ResourceBlock,
  relation: string
): Option<string> {
  if (!startsWithLetter(relation)) {
    return `Relation name "${relation}" must start with a letter`;
  }
  if (!relation.match(/^[0-9a-zA-Z_]+$/)) {
    return `Can't add relation named "${relation}" as relation name can only contain letters, numbers, and underscores`;
  }

  return checkIfValidDeclaration(resource, relation);
}

export function checkIfValidResource(
  resources: ResourceBlocks,
  resource: string
): Option<string> {
  const reserved_names = ["actor", "resource", "global", "Actor", "Resource"];

  if (!startsWithCapital(resource)) {
    return `Resource name "${resource}" must start with an Upper Case letter`;
  }
  if (reserved_names.includes(resource)) {
    return `Can't add resource named as one of these reserved names: "${reserved_names}"`;
  }
  if (resources.some(({ name }) => name === resource)) {
    return `Can't add resource named "${resource}" as there is already a resource with that name`;
  }
  if (!resource.match(/^[0-9a-zA-Z]+$/)) {
    return `Can't add resource named "${resource}" as resource name can only contain letters and numbers`;
  }
  return null;
}

export function workbenchPolicyReducer(
  emitError: (error: string) => void,
  segmentCallback: (args: any) => void,
  policy: WorkbenchPolicy,
  action: WorkbenchPolicyAction
): WorkbenchPolicy {
  segmentCallback({ interaction: action.action, action });
  switch (action.action) {
    case "add role": {
      const { role } = action;
      const resource = policy.resources[action.resource];
      const error = checkIfValidDeclaration(resource, role);
      if (error) {
        emitError(error);
        return policy;
      }
      const roles = [...resource.roles, role];
      const matrix = resource.matrix.map((arr) => [...arr, false]);
      let rolesImplicationMatrix = policy.resources[
        action.resource
      ].rolesImplicationMatrix.map((arr) => [...arr, false]);
      rolesImplicationMatrix = [
        ...rolesImplicationMatrix,
        roles.map((_) => false),
      ];
      const globalRolesMatrix = [
        ...resource.globalRolesMatrix,
        (policy.global?.roles ?? []).map((_) => false),
      ];
      const relations = resource.relations.map((relation) => {
        const related = policy.resources.find(
          ({ name }) => name === relation.related_resource
        );
        if (!related) {
          emitError(
            `Could not find related resource "${relation.related_resource}"`
          );
          return relation;
        }
        const matrix = [...relation.matrix, related.roles.map((_) => false)];
        return { ...relation, matrix };
      });

      const resources = updateIdx(policy.resources, action.resource, {
        ...resource,
        roles,
        matrix,
        rolesImplicationMatrix,
        globalRolesMatrix,
        relations,
      }).map((res) => {
        const relations = res.relations.map((relation) => {
          if (relation.related_resource === resource.name) {
            const matrix = relation.matrix.map((row) => [...row, false]);
            return { ...relation, matrix };
          } else {
            return relation;
          }
        });
        return { ...res, relations };
      });
      return { ...policy, resources };
    }
    case "add perm": {
      const { perm } = action;
      const resource = policy.resources[action.resource];
      const error = checkIfValidDeclaration(resource, perm);
      if (error) {
        emitError(error);
        return policy;
      }
      const perms = [...resource.perms, perm];
      const matrix = [...resource.matrix, resource.roles.map((_) => false)];

      const resources = updateIdx(policy.resources, action.resource, {
        ...resource,
        perms,
        matrix,
      });
      return { ...policy, resources };
    }
    case "reset": {
      return DEFAULT_WORKBENCH_POLICY;
    }
    case "add resource": {
      const error = checkIfValidResource(policy.resources, action.resource);
      if (error) {
        emitError(error);
        return policy;
      }

      const new_resource: ResourceBlock = {
        name: action.resource,
        roles: [],
        perms: [],
        matrix: [],
        rolesImplicationMatrix: [],
        relations: [],
        globalRolesMatrix: [],
      };
      const resources = [...policy.resources, new_resource];
      return { ...policy, resources };
    }
    case "set perms checkbox": {
      const { i, j, value } = action;
      const resource = policy.resources[action.resource];
      const matrix = updateIdx(
        resource.matrix,
        i,
        updateIdx(resource.matrix[i], j, value)
      );
      const resources = updateIdx(policy.resources, action.resource, {
        ...resource,
        matrix,
      });
      return { ...policy, resources };
    }
    case "set roles checkbox": {
      const { i, j, value } = action;
      if (i === j) {
        emitError("A role always implies itself");
        return policy;
      }
      const resource = policy.resources[action.resource];
      const rolesImplicationMatrix = updateIdx(
        resource.rolesImplicationMatrix,
        i,
        updateIdx(resource.rolesImplicationMatrix[i], j, value)
      );
      const resources = updateIdx(policy.resources, action.resource, {
        ...resource,
        rolesImplicationMatrix,
      });
      return { ...policy, resources };
    }
    case "set relations checkbox": {
      const { relation, i, j, value } = action;
      const resource = policy.resources[action.resource];
      const matrix = updateIdx(
        resource.relations[relation].matrix,
        i,
        updateIdx(resource.relations[relation].matrix[i], j, value)
      );
      const relations = updateIdx(resource.relations, relation, {
        ...resource.relations[relation],
        matrix,
      });
      const resources = updateIdx(policy.resources, action.resource, {
        ...resource,
        relations,
      });
      return { ...policy, resources };
    }
    case "edit perm": {
      const resource = policy.resources[action.resource];
      const error =
        action.name !== resource.perms[action.perm] &&
        checkIfValidDeclaration(resource, action.name);
      if (error) {
        emitError(error);
        return policy;
      }
      const perms = updateIdx(resource.perms, action.perm, action.name);
      const resources = updateIdx(policy.resources, action.resource, {
        ...resource,
        perms,
      });
      return { ...policy, resources };
    }
    case "edit role": {
      const resource = policy.resources[action.resource];
      const error =
        action.name !== resource.roles[action.role] &&
        checkIfValidDeclaration(resource, action.name);
      if (error) {
        emitError(error);
        return policy;
      }
      const roles = updateIdx(resource.roles, action.role, action.name);
      const resources = updateIdx(policy.resources, action.resource, {
        ...resource,
        roles,
      });
      return { ...policy, resources };
    }
    case "add relation": {
      const resource = policy.resources[action.resource];
      const { relation, related_resource } = action;
      const error = checkIfValidRelation(resource, relation);
      if (error) {
        emitError(error);
        return policy;
      }
      const roles = policy.resources.find(
        ({ name }) => name === related_resource
      )?.roles;
      if (!roles) {
        emitError(`There is no resource named "${related_resource}"`);
        return policy;
      }
      const relations = [
        ...resource.relations,
        {
          relation_name: relation,
          related_resource,
          matrix: resource.roles.map((_) => roles.map((_) => false)),
        },
      ];
      const resources = updateIdx(policy.resources, action.resource, {
        ...resource,
        relations,
      });
      return { ...policy, resources };
    }
    case "edit resource relation name": {
      const resource = policy.resources[action.resource];
      const { relation_name, updated_name } = action;
      const error =
        relation_name !== updated_name &&
        checkIfValidRelation(resource, updated_name);
      if (error) {
        emitError(error);
        return policy;
      }

      const relations = resource.relations.map((relation) => {
        if (relation.relation_name === relation_name) {
          const relation_name = updated_name;
          return { ...relation, relation_name };
        } else {
          return relation;
        }
      });

      const resources = updateIdx(policy.resources, action.resource, {
        ...resource,
        relations,
      });
      return { ...policy, resources };
    }
    case "delete resource relation": {
      const resource = policy.resources[action.resource];
      const relations = resource.relations.filter(
        (relation) => relation.relation_name !== action.relation_name
      );

      const resources = updateIdx(policy.resources, action.resource, {
        ...resource,
        relations,
      });
      return { ...policy, resources };
    }
    case "edit resource name": {
      const resource = policy.resources[action.resource];
      const name = action.name;
      const prev_resource_name = resource.name;
      const error =
        name !== prev_resource_name &&
        checkIfValidResource(policy.resources, action.name);
      if (error) {
        emitError(error);
        return policy;
      }

      const resources = updateIdx(policy.resources, action.resource, {
        ...resource,
        name,
      }).map((res) => {
        const relations = res.relations.map((relation) => {
          if (relation.related_resource === prev_resource_name) {
            const related_resource = action.name;
            return { ...relation, related_resource };
          } else {
            return relation;
          }
        });
        return { ...res, relations };
      });
      return { ...policy, resources };
    }
    case "delete resource": {
      let resources = policy.resources.map((res) => {
        const relations = res.relations.filter(
          (relation) =>
            relation.related_resource !== policy.resources[action.resource].name
        );
        return { ...res, relations };
      });
      resources = deleteIdx(resources, action.resource);
      return { ...policy, resources };
    }
    case "delete role": {
      const resource = policy.resources[action.resource];
      const roles = deleteIdx(resource.roles, action.role);
      const matrix = resource.matrix.map((arr) => deleteIdx(arr, action.role));
      let rolesImplicationMatrix = policy.resources[
        action.resource
      ].rolesImplicationMatrix.map((arr) => deleteIdx(arr, action.role));
      rolesImplicationMatrix = deleteIdx(rolesImplicationMatrix, action.role);

      const relations = resource.relations.map((relation) => {
        return { ...relation, matrix: deleteIdx(relation.matrix, action.role) };
      });

      const resources = updateIdx(policy.resources, action.resource, {
        ...resource,
        roles,
        matrix,
        rolesImplicationMatrix,
        relations,
      }).map((res) => {
        const relations = res.relations.map((relation) => {
          if (relation.related_resource === resource.name) {
            let matrix = relation.matrix.map((row) =>
              deleteIdx(row, action.role)
            );

            return { ...relation, matrix };
          } else {
            return relation;
          }
        });

        // Remove it from global roles implications, too
        const globalRolesMatrix = deleteIdx(
          resource.globalRolesMatrix,
          action.role
        );

        return { ...res, relations, globalRolesMatrix };
      });

      return { ...policy, resources };
    }
    case "delete perm": {
      const resource = policy.resources[action.resource];
      const perms = deleteIdx(resource.perms, action.perm);
      const matrix = deleteIdx(resource.matrix, action.perm);
      const relations = policy.resources[action.resource].relations.map(
        (relation) => {
          let matrix = relation.matrix;
          matrix = deleteIdx(matrix, action.perm);
          return {
            ...relation,
            matrix,
          };
        }
      );

      const resources = updateIdx(policy.resources, action.resource, {
        ...resource,
        perms,
        matrix,
        relations,
      });
      return { ...policy, resources };
    }
    case "add global block": {
      return {
        ...policy,
        global: {
          roles: [],
          perms: [],
          matrix: [],
          rolesImplicationMatrix: [],
        },
      };
    }
    case "delete global block": {
      const resources = policy.resources.map((resource) => ({
        ...resource,
        globalRolesMatrix: resource.globalRolesMatrix.map((_) => []),
      }));
      const global = null;
      return { ...policy, resources, global };
    }
    case "add global perm": {
      if (!policy.global) {
        emitError("There is no global block.");
        return policy;
      }
      const error = checkIfValidGlobalDeclaration(policy.global, action.perm);
      if (error) {
        emitError(error);
        return policy;
      }
      const perms = [...policy.global.perms, action.perm];
      const matrix = [
        ...policy.global.matrix,
        policy.global.roles.map((_) => false),
      ];
      return { ...policy, global: { ...policy.global, perms, matrix } };
    }
    case "add global role": {
      if (!policy.global) {
        emitError("There is no global block.");
        return policy;
      }
      const error = checkIfValidGlobalDeclaration(policy.global, action.role);
      if (error) {
        emitError(error);
        return policy;
      }
      const roles = [...policy.global.roles, action.role];
      const rolesImplicationMatrix = [
        ...policy.global.rolesImplicationMatrix.map((row) => [...row, false]),
        roles.map((_) => false),
      ];
      const matrix = policy.global.matrix.map((row) => [...row, false]);
      const resources = policy.resources.map((resource) => {
        const globalRolesMatrix = resource.globalRolesMatrix.map((row) => [
          ...row,
          false,
        ]);
        return { ...resource, globalRolesMatrix };
      });
      return {
        ...policy,
        resources,
        global: {
          ...policy.global,
          roles,
          matrix,
          rolesImplicationMatrix,
        },
      };
    }
    case "set global perms checkbox": {
      if (!policy.global) {
        emitError("There is no global block.");
        return policy;
      }
      const { i, j, value } = action;
      const matrix = updateIdx(
        policy.global.matrix,
        i,
        updateIdx(policy.global.matrix[i], j, value)
      );

      return { ...policy, global: { ...policy.global, matrix } };
    }
    case "set global roles checkbox": {
      if (!policy.global) {
        emitError("There is no global block.");
        return policy;
      }
      const { i, j, value } = action;
      if (i === j) {
        emitError("A role always implies itself");
        return policy;
      }
      const rolesImplicationMatrix = updateIdx(
        policy.global.rolesImplicationMatrix,
        i,
        updateIdx(policy.global.rolesImplicationMatrix[i], j, value)
      );
      return {
        ...policy,
        global: { ...policy.global, rolesImplicationMatrix },
      };
    }
    case "edit global perm": {
      if (!policy.global) {
        emitError("There is no global block.");
        return policy;
      }
      const error =
        action.name !== policy.global.perms[action.perm] &&
        checkIfValidGlobalDeclaration(policy.global, action.name);
      if (error) {
        emitError(error);
        return policy;
      }
      const perms = updateIdx(policy.global.perms, action.perm, action.name);
      return { ...policy, global: { ...policy.global, perms } };
    }
    case "edit global role": {
      if (!policy.global) {
        emitError("There is no global block.");
        return policy;
      }
      const error =
        action.name !== policy.global.roles[action.role] &&
        checkIfValidGlobalDeclaration(policy.global, action.name);
      if (error) {
        emitError(error);
        return policy;
      }
      const roles = updateIdx(policy.global.roles, action.role, action.name);
      return { ...policy, global: { ...policy.global, roles } };
    }
    case "delete global perm": {
      if (!policy.global) {
        emitError("There is no global block.");
        return policy;
      }
      const perms = deleteIdx(policy.global.perms, action.perm);
      return { ...policy, global: { ...policy.global, perms } };
    }
    case "delete global role": {
      if (!policy.global) {
        emitError("There is no global block.");
        return policy;
      }
      // Clean up everything we added in "add global role":
      // Delete role from list of global roles
      const roles = deleteIdx(policy.global.roles, action.role);
      // Delete `action.row`th row and column from global roles implications
      const rolesImplicationMatrix = deleteIdx(
        policy.global.rolesImplicationMatrix.map((row) =>
          deleteIdx(row, action.role)
        ),
        action.role
      );
      // Delete `action.row`th column from global permissions matrix
      const matrix = policy.global.matrix.map((row) =>
        deleteIdx(row, action.role)
      );
      // Delete the `action.row`th column from every resource's global rows matrix
      const resources = policy.resources.map((resource) => {
        const globalRolesMatrix = resource.globalRolesMatrix.map((row) =>
          deleteIdx(row, action.role)
        );
        return { ...resource, globalRolesMatrix };
      });

      return {
        ...policy,
        resources,
        global: {
          ...policy.global,
          roles,
          matrix,
          rolesImplicationMatrix,
        },
      };
    }
    case "set resource role global role checkbox": {
      const resource = policy.resources[action.resource];
      const globalRolesMatrix = updateIdx(
        resource.globalRolesMatrix,
        action.i,
        updateIdx(resource.globalRolesMatrix[action.i], action.j, action.value)
      );
      const resources = updateIdx(policy.resources, action.resource, {
        ...resource,
        globalRolesMatrix,
      });
      return { ...policy, resources };
    }
    case "add test block": {
      const existingIdx = policy.tests.findIndex(
        (test) => test.name === action.name
      );
      if (existingIdx === -1) {
        const tests = [
          ...policy.tests,
          {
            name: action.name,
            setup: [],
            assertions: [],
          },
        ];
        return { ...policy, tests };
      } else {
        if (action.replace) {
          const tests = updateIdx(policy.tests, existingIdx, {
            name: action.name,
            setup: [],
            assertions: [],
          });
          return { ...policy, tests };
        } else {
          emitError(`A test named ${action.name} already exists.`);
          return policy;
        }
      }
    }
    case "edit test block": {
      const tests = updateIdx(policy.tests, action.block, {
        ...policy.tests[action.block],
        name: action.name,
      });
      return { ...policy, tests };
    }
    case "delete test block": {
      const tests = deleteIdx(policy.tests, action.block);
      return { ...policy, tests };
    }
    case "add test setup fact": {
      const oldBlock = policy.tests[action.block];
      const newBlock = { ...oldBlock, setup: [...oldBlock.setup, action.fact] };
      const tests = updateIdx(policy.tests, action.block, newBlock);
      return { ...policy, tests };
    }
    case "edit test setup fact": {
      const oldBlock = policy.tests[action.block];
      const newBlock = {
        ...oldBlock,
        setup: updateIdx(oldBlock.setup, action.fact, action.value),
      };
      const tests = updateIdx(policy.tests, action.block, newBlock);
      return { ...policy, tests };
    }
    case "delete test setup fact": {
      const oldBlock = policy.tests[action.block];
      const newBlock = {
        ...oldBlock,
        setup: deleteIdx(oldBlock.setup, action.fact),
      };
      const tests = updateIdx(policy.tests, action.block, newBlock);
      return { ...policy, tests };
    }
    case "add test assertion": {
      const oldBlock = policy.tests[action.block];
      const newBlock = {
        ...oldBlock,
        assertions: [...oldBlock.assertions, action.assertion],
      };
      const tests = updateIdx(policy.tests, action.block, newBlock);
      return { ...policy, tests };
    }
    case "edit test assertion": {
      const oldBlock = policy.tests[action.block];
      const newBlock = {
        ...oldBlock,
        assertions: updateIdx(
          oldBlock.assertions,
          action.assertion,
          action.value
        ),
      };
      const tests = updateIdx(policy.tests, action.block, newBlock);
      return { ...policy, tests };
    }
    case "delete test assertion": {
      const oldBlock = policy.tests[action.block];
      const newBlock = {
        ...oldBlock,
        assertions: deleteIdx(oldBlock.assertions, action.assertion),
      };
      const tests = updateIdx(policy.tests, action.block, newBlock);
      return { ...policy, tests };
    }
    case "add actor": {
      if (policy.actors.find((actor) => actor.name === action.name)) {
        emitError(`There is already an actor with the name "${action.name}".`);
        return policy;
      }
      const actors: ActorBlocks = [...policy.actors, { name: action.name }];
      return { ...policy, actors };
    }
    case "edit actor name": {
      if (policy.actors.find((actor) => actor.name === action.name)) {
        emitError(`There is already an actor with the name "${action.name}".`);
        return policy;
      }
      const oldActor = policy.actors[action.actor];
      const newActor = { ...oldActor, name: action.name };
      const actors: ActorBlocks = updateIdx(
        policy.actors,
        action.actor,
        newActor
      );
      return { ...policy, actors };
    }
    case "delete actor": {
      const [actorsHead, ...actorsTail] = deleteIdx(
        policy.actors,
        action.actor
      );
      if (actorsHead) {
        return { ...policy, actors: [actorsHead, ...actorsTail] };
      } else {
        emitError("Cannot delete the last actor.");
        return policy;
      }
    }
    case "add rule": {
      const rules = [
        ...policy.rules,
        { description: action.description, source: action.source },
      ];
      return { ...policy, rules };
    }
    case "edit rule description": {
      const oldRule = policy.rules[action.rule];
      const newRule = { ...oldRule, description: action.description };
      const rules = updateIdx(policy.rules, action.rule, newRule);
      return { ...policy, rules };
    }
    case "delete rule": {
      const rules = deleteIdx(policy.rules, action.rule);
      return { ...policy, rules };
    }
    case "batch": {
      return action.actions.reduce(
        workbenchPolicyReducer.bind(null, emitError, segmentCallback),
        policy
      );
    }
  }
}

const usePersistedState = createPersistedState<
  Partial<{
    [env: string]: WorkbenchPolicy;
  }>
>("workbench_v2");

export function usePersistedWorkbenchState(
  selectedEnv: EnvData
): [WorkbenchPolicy, (resources: WorkbenchPolicy) => void] {
  const [states, setStates] = usePersistedState({});
  const envState = states[selectedEnv.pub_id];
  const value = envState
    ? envState.workbenchVersion === CURRENT_WORKBENCH_STATE_VERSION
      ? envState
      : migrateWorkbenchState(envState)
    : DEFAULT_WORKBENCH_POLICY;
  return [
    value,
    (workbenchPolicy) => {
      setStates({
        ...states,
        [selectedEnv.pub_id]: defaultPolicyIfEmpty(workbenchPolicy),
      });
    },
  ];
}

export type StarterPolicyInput = {
  tenant: string; // B2B_CUSTOMER_TENANT
  actor: string; // B2B_CUSTOMER_ACTOR
  businessObject: string;
};

type PersistedExtraState = {
  /** track closed rather than open so that panels default to open */
  closedEditorPanels: { [persistenceKey: string]: null };
  starterPolicy: Option<StarterPolicyInput>;
  /** @deprecated actors are now stored in main workbench state */
  actorName: Option<string>;
  selectedTabs: { [persistenceKey: string]: number };
  panelWidth: number;
};

const usePersistedExtraState = createPersistedState<
  Partial<{
    [env: string]: Partial<PersistedExtraState>;
  }>
>("workbench_extra");

export function usePersistedExtraWorkbenchState<
  T extends keyof PersistedExtraState
>(
  selectedEnv: EnvData,
  key: T,
  defaultValue: PersistedExtraState[T]
): [PersistedExtraState[T], (state: PersistedExtraState[T]) => void] {
  const [states, setStates] = usePersistedExtraState({});
  const envState = states[selectedEnv.pub_id] ?? {};
  const setter = (state: PersistedExtraState[T]) =>
    setStates({
      ...states,
      [selectedEnv.pub_id]: { ...envState, [key]: state },
    });
  const value = envState[key] ?? defaultValue;
  return [value, setter];
}

export function usePersistedWorkbenchReducer(
  selectedEnv: EnvData,
  reducer: Reducer<WorkbenchPolicy, WorkbenchPolicyAction>
): {
  workbenchPolicy: WorkbenchPolicy;
  dispatch: Dispatch<WorkbenchPolicyAction>;
  undo: undefined | (() => void);
  redo: undefined | (() => void);
} {
  const [state, setState] = usePersistedWorkbenchState(selectedEnv);

  // we push and pop at the front (index 0)
  const [stacks, setStacks] = useState<{
    undo: WorkbenchPolicy[];
    redo: WorkbenchPolicy[];
  }>({
    undo: [],
    redo: [],
  });
  const undo =
    stacks.undo.length > 0
      ? () => {
          const [newState, ...undo] = stacks.undo;
          const redo = [state, ...stacks.redo];
          setStacks({ undo, redo });
          setState(newState);
        }
      : undefined;
  const redo =
    stacks.redo.length > 0
      ? () => {
          const [newState, ...redo] = stacks.redo;
          const undo = [state, ...stacks.undo];
          setStacks({ undo, redo });
          setState(newState);
        }
      : undefined;

  return {
    workbenchPolicy: state,
    dispatch(action) {
      const newState = reducer(state, action);
      const undo = [state, ...stacks.undo];
      const redo = new Array();
      setStacks({ undo, redo });
      setState(newState);
    },
    undo,
    redo,
  };
}

/** parallel to the test block's setup and assertion */
export type TestBlockRun = {
  success: boolean;
  passed: number;
  total: number;
  setup_errors: string[];
  assertion_messages: Result<null, string>[];
};

export async function runTestBlock(
  selectedEnv: EnvData,
  workbenchPolicy: WorkbenchPolicy,
  testIdx: number
): Promise<TestBlockRun> {
  const testBlock = workbenchPolicy.tests[testIdx];
  const src = workbenchToPolicy({ ...workbenchPolicy, tests: [testBlock] });
  /** the line number the `test` block begins on */
  const testLine = src.split("\n").findIndex((line) => line.match(/^test/)) + 3;
  /** the line number the first assertion is on */
  const assertionsLine = testLine + testBlock.setup.length + 1;
  const policyResults: PolicyTestResult = await (
    await fetch(`/web/environment/${selectedEnv.pub_id}/api/test_policy?full`, {
      method: "POST",
      body: JSON.stringify({
        src,
      }),
    })
  ).json();
  const { success } = policyResults;
  const setup_errors: string[] = [];
  const assertion_messages: Result<null, string>[] = [];
  // process policyResults.errors and policyResults.tests[0].passed_assertions;
  // we don't need to process policyResults.tests[0].failed_assertions because
  // policyResults.errors subsumes it
  for (const error of policyResults.errors) {
    if (!error.position) {
      continue;
    }
    const errorLine = error.position[0][0] + 1;
    if (errorLine < assertionsLine) {
      // it's in the setup block
      setup_errors[errorLine - testLine] = error.message_for_editors;
    } else {
      // it's in an assertion
      assertion_messages[errorLine - assertionsLine] = {
        ok: false,
        error: error.message_for_editors,
      };
    }
  }
  let passed = 0;
  let total = 0;
  const test = policyResults.tests[0];
  if (test) {
    ({ passed, total } = test);
    for (const assertion of test.passed_assertions) {
      const assertionLine = assertion[0][0] + 1;
      assertion_messages[assertionLine - assertionsLine] = {
        ok: true,
        value: null,
      };
    }
  }
  // if there are any gaps in assertion_messages, they represent failed assertions,
  // so we fill them in with error messages.
  // this happens when there are errors in the setup block
  for (let i = 0; i < testBlock.assertions.length; i++) {
    assertion_messages[i] ??= {
      ok: false,
      error: "Unable to run assertion, likely due to a previous error.",
    };
  }
  return {
    success,
    passed,
    total,
    setup_errors,
    assertion_messages: assertion_messages,
  };
}

export function addCommonRolesActions(
  resource: number
): WorkbenchPolicyAction[] {
  return [
    { action: "add role", resource, role: "viewer" },
    { action: "add role", resource, role: "owner" },
  ];
}

export function addCommonRolesAndPermissionsActions(
  resource: number
): WorkbenchPolicyAction[] {
  return [
    ...addCommonRolesActions(resource),
    { action: "add perm", resource, perm: "view" },
    { action: "add perm", resource, perm: "edit" },
    {
      action: "set perms checkbox",
      resource,
      i: 0,
      j: 0,
      value: true,
    },
    {
      action: "set roles checkbox",
      resource,
      i: 0,
      j: 1,
      value: true,
    },
    {
      action: "set perms checkbox",
      resource,
      i: 1,
      j: 1,
      value: true,
    },
  ];
}

export const ADD_GLOBAL_BLOCK_ACTIONS: WorkbenchPolicyAction[] = [
  { action: "add global block" },
  { action: "add global role", role: "member" },
  { action: "add global role", role: "admin" },
  { action: "add global perm", perm: "view" },
  { action: "add global perm", perm: "create" },
  {
    action: "set global perms checkbox",
    i: 0,
    j: 0,
    value: true,
  },
  {
    action: "set global roles checkbox",
    i: 0,
    j: 1,
    value: true,
  },
  {
    action: "set global perms checkbox",
    i: 1,
    j: 1,
    value: true,
  },
];

export function addResourceRolesAndPermissionsTestActions(
  workbenchPolicy: WorkbenchPolicy,
  resourceIdx: number
): WorkbenchPolicyAction[] {
  const resource = workbenchPolicy.resources[resourceIdx];
  const { name, roles, perms } = resource;
  const existingBlock = workbenchPolicy.tests.findIndex(
    (test) => test.name === `${name} roles and permissions`
  );
  const block =
    existingBlock === -1 ? workbenchPolicy.tests.length : existingBlock;
  const actor = workbenchPolicy.actors[0].name;
  return [
    {
      action: "add test block",
      name: `${name} roles and permissions`,
      replace: true,
    },
    ...roles.map(
      (role, roleIdx): WorkbenchPolicyAction => ({
        action: "add test setup fact",
        block,
        fact: {
          predicate: "has_role",
          args: [
            { type: actor, id: exampleUserName(roleIdx) },
            { type: "String", id: role },
            { type: name, id: "example" },
          ],
        },
      })
    ),
    ...perms.flatMap((perm, permIdx): WorkbenchPolicyAction[] =>
      roles.map(
        (_, roleIdx): WorkbenchPolicyAction => ({
          action: "add test assertion",
          block,
          assertion: {
            expected: roleGrantsPermission(resource, roleIdx, permIdx),
            fact: {
              predicate: "allow",
              args: [
                { type: actor, id: exampleUserName(roleIdx) },
                { type: "String", id: perm },
                { type: name, id: "example" },
              ],
            },
          },
        })
      )
    ),
  ];
}

export function addResourceRelationTestActions(
  workbenchPolicy: WorkbenchPolicy,
  resourceIdx: number,
  relationIdx: number
): WorkbenchPolicyAction[] {
  const resource = workbenchPolicy.resources[resourceIdx];
  const relation = resource.relations[relationIdx];
  const relatedResource = workbenchPolicy.resources.find(
    (res) => res.name === relation.related_resource
  )!;
  const existingBlock = workbenchPolicy.tests.findIndex(
    (test) =>
      test.name === `${resource.name} ${relation.relation_name} relation`
  );
  const block =
    existingBlock === -1 ? workbenchPolicy.tests.length : existingBlock;
  const actor = workbenchPolicy.actors[0].name;
  return [
    {
      action: "add test block",
      name: `${resource.name} ${relation.relation_name} relation`,
      replace: true,
    },
    {
      action: "add test setup fact",
      block,
      fact: {
        predicate: "has_relation",
        args: [
          { type: resource.name, id: "example" },
          { type: "String", id: relation.relation_name },
          {
            type: relatedResource.name,
            id: `${relation.relation_name}${relation.related_resource}`,
          },
        ],
      },
    },
    ...relatedResource.roles.map(
      (relatedRole, relatedRoleIdx): WorkbenchPolicyAction => ({
        action: "add test setup fact",
        block,
        fact: {
          predicate: "has_role",
          args: [
            { type: actor, id: exampleUserName(relatedRoleIdx) },
            { type: "String", id: relatedRole },
            {
              type: relation.related_resource,
              id: `${relation.relation_name}${relation.related_resource}`,
            },
          ],
        },
      })
    ),
    ...resource.perms.flatMap((perm, permIdx): WorkbenchPolicyAction[] =>
      relatedResource.roles.map(
        (_, relatedRoleIdx): WorkbenchPolicyAction => ({
          action: "add test assertion",
          block,
          assertion: {
            expected: relation.matrix.some((row, impliedRoleIdx) =>
              row.some(
                (checked, impliedRelatedRoleIdx) =>
                  checked &&
                  roleImpliesRole(
                    relatedResource,
                    relatedRoleIdx,
                    impliedRelatedRoleIdx
                  ) &&
                  roleGrantsPermission(resource, impliedRoleIdx, permIdx)
              )
            ),
            fact: {
              predicate: "allow",
              args: [
                { type: actor, id: exampleUserName(relatedRoleIdx) },
                { type: "String", id: perm },
                { type: resource.name, id: "example" },
              ],
            },
          },
        })
      )
    ),
  ];
}

export function addResourceGlobalRolesTestActions(
  workbenchPolicy: WorkbenchPolicy,
  resourceIdx: number
): WorkbenchPolicyAction[] {
  const resource = workbenchPolicy.resources[resourceIdx];
  const existingBlock = workbenchPolicy.tests.findIndex(
    (test) => test.name === `${resource.name} global roles`
  );
  const block =
    existingBlock === -1 ? workbenchPolicy.tests.length : existingBlock;
  const actor = workbenchPolicy.actors[0].name;
  return [
    {
      action: "add test block",
      name: `${resource.name} global roles`,
      replace: true,
    },
    ...workbenchPolicy.global!.roles.map(
      (globalRole, globalRoleIdx): WorkbenchPolicyAction => ({
        action: "add test setup fact",
        block,
        fact: {
          predicate: "has_role",
          args: [
            { type: actor, id: exampleUserName(globalRoleIdx) },
            { type: "String", id: globalRole },
          ],
        },
      })
    ),
    ...resource.globalRolesMatrix.flatMap(
      (row, permIdx): WorkbenchPolicyAction[] =>
        row.map(
          (_, globalRoleIdx): WorkbenchPolicyAction => ({
            action: "add test assertion",
            block,
            assertion: {
              expected: resource.globalRolesMatrix.some((row, impliedRoleIdx) =>
                row.some(
                  (checked, impliedGlobalRoleIdx) =>
                    checked &&
                    roleImpliesRole(
                      workbenchPolicy.global!,
                      globalRoleIdx,
                      impliedGlobalRoleIdx
                    ) &&
                    roleGrantsPermission(resource, impliedRoleIdx, permIdx)
                )
              ),
              fact: {
                predicate: "allow",
                args: [
                  { type: actor, id: exampleUserName(globalRoleIdx) },
                  { type: "String", id: resource.perms[permIdx] },
                  { type: resource.name, id: "example" },
                ],
              },
            },
          })
        )
    ),
  ];
}
